import React from 'react';
import { useWizard } from '../../context/WizardContext';
import NavigationButtons from '../shared/NavigationButtons';
import { Users, AlertTriangle, Info } from 'lucide-react';

const VULNERABLE_GROUPS = [
  { id: 'children', label: 'Children (under 18 years)' },
  { id: 'elderly', label: 'Elderly individuals' },
  { id: 'pregnant', label: 'Pregnant women' },
  { id: 'prisoners', label: 'Prisoners or detained persons' },
  { id: 'mentalHealth', label: 'People with mental health conditions' },
  { id: 'cognitive', label: 'People with cognitive impairments' },
  { id: 'disability', label: 'People with disabilities' },
  { id: 'economicallyDisadvantaged', label: 'Economically disadvantaged groups' },
  { id: 'refugees', label: 'Refugees or asylum seekers' },
  { id: 'indigenous', label: 'Indigenous communities' },
  { id: 'employees', label: 'Employees or subordinates (power imbalance)' },
  { id: 'students', label: 'Students of the researcher' },
  { id: 'other', label: 'Other vulnerable group' }
];

function D4_Participants() {
  const { dataE, dataA, updateDataRaw, nextStep, previousStep } = useWizard();
  const participants = dataE.participants;

  const updateParticipants = (field, value) => {
    updateDataRaw({
      'wizardE.participants': { ...participants, [field]: value }
    });
  };

  const updateVulnerableGroups = (field, value) => {
    updateDataRaw({
      'wizardE.participants': {
        ...participants,
        vulnerableGroups: { ...participants.vulnerableGroups, [field]: value }
      }
    });
  };

  const toggleVulnerableType = (typeId) => {
    const currentTypes = participants.vulnerableGroups.types || [];
    const newTypes = currentTypes.includes(typeId)
      ? currentTypes.filter(t => t !== typeId)
      : [...currentTypes, typeId];
    updateVulnerableGroups('types', newTypes);
  };

  const getSampleSizeFromWizardA = () => {
    if (dataA.techniques?.sampleSizesPerMethod) {
      const sizes = Object.values(dataA.techniques.sampleSizesPerMethod);
      if (sizes.length > 0) {
        const total = sizes.reduce((sum, size) => sum + (parseInt(size) || 0), 0);
        return total;
      }
    }
    return null;
  };

  const wizardASampleSize = getSampleSizeFromWizardA();

  const isComplete = participants.description && 
                     participants.inclusionCriteria &&
                     participants.vulnerableGroups.includes !== null &&
                     (!participants.vulnerableGroups.includes || participants.vulnerableGroups.justification);

  return (
    <div className="wizard-step">
      <div className="step-header">
        <Users className="step-icon" size={32} />
        <div>
          <h2>Step 4: Participants</h2>
          <p className="step-description">
            Describe who will participate in your research and how they will be selected.
          </p>
        </div>
      </div>

      <div className="form-section">
        <h3>Participant Population</h3>

        <div className="form-group">
          <label>Description of Participants *</label>
          <textarea
            value={participants.description}
            onChange={(e) => updateParticipants('description', e.target.value)}
            placeholder="Describe who your participants will be (e.g., age range, profession, characteristics)"
            rows={4}
          />
        </div>

        <div className="form-group">
          <label>How will participants be identified and selected?</label>
          <textarea
            value={participants.selectionMethod}
            onChange={(e) => updateParticipants('selectionMethod', e.target.value)}
            placeholder="Describe how you will identify and recruit participants"
            rows={3}
          />
        </div>

        <div className="form-group">
          <label>Sample Size Justification</label>
          <textarea
            value={participants.sampleSizeJustification}
            onChange={(e) => updateParticipants('sampleSizeJustification', e.target.value)}
            placeholder="Explain how you determined your sample size and why it is appropriate"
            rows={3}
          />
          {wizardASampleSize && (
            <div className="info-box info" style={{ marginTop: '0.5rem' }}>
              <Info size={16} />
              <span>
                From Wizard A: Your planned sample size is approximately {wizardASampleSize} participants.
              </span>
            </div>
          )}
        </div>
      </div>

      <div className="form-section">
        <h3>Inclusion and Exclusion Criteria</h3>

        <div className="form-group">
          <label>Inclusion Criteria *</label>
          <textarea
            value={participants.inclusionCriteria}
            onChange={(e) => updateParticipants('inclusionCriteria', e.target.value)}
            placeholder="List the criteria that participants must meet to be included in the study"
            rows={4}
          />
          <small className="field-help">
            e.g., Age 18+, English speaking, employed in healthcare sector
          </small>
        </div>

        <div className="form-group">
          <label>Exclusion Criteria</label>
          <textarea
            value={participants.exclusionCriteria}
            onChange={(e) => updateParticipants('exclusionCriteria', e.target.value)}
            placeholder="List any criteria that would exclude someone from participating"
            rows={4}
          />
          <small className="field-help">
            e.g., Unable to provide informed consent, currently undergoing treatment for X
          </small>
        </div>
      </div>

      <div className="form-section">
        <h3>Vulnerable Groups</h3>

        <div className="eligibility-question">
          <label className="question-label">
            Does your research involve any vulnerable groups? *
          </label>
          <p className="question-help">
            Vulnerable groups require additional ethical considerations and safeguards.
          </p>
          <div className="radio-group horizontal">
            <label className="radio-option">
              <input
                type="radio"
                name="vulnerableGroups"
                checked={participants.vulnerableGroups.includes === true}
                onChange={() => updateVulnerableGroups('includes', true)}
              />
              <span className="radio-label">Yes</span>
            </label>
            <label className="radio-option">
              <input
                type="radio"
                name="vulnerableGroups"
                checked={participants.vulnerableGroups.includes === false}
                onChange={() => updateVulnerableGroups('includes', false)}
              />
              <span className="radio-label">No</span>
            </label>
          </div>
        </div>

        {participants.vulnerableGroups.includes === true && (
          <>
            <div className="info-box warning">
              <AlertTriangle size={20} />
              <div>
                <strong>Additional Scrutiny Required</strong>
                <p>
                  Research involving vulnerable groups typically requires enhanced ethical review 
                  and additional safeguards to protect participants.
                </p>
              </div>
            </div>

            <div className="form-group">
              <label>Select the vulnerable groups involved:</label>
              <div className="checkbox-grid">
                {VULNERABLE_GROUPS.map(group => (
                  <label key={group.id} className="checkbox-option">
                    <input
                      type="checkbox"
                      checked={participants.vulnerableGroups.types?.includes(group.id) || false}
                      onChange={() => toggleVulnerableType(group.id)}
                    />
                    <span>{group.label}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="form-group">
              <label>Justification and Safeguards *</label>
              <textarea
                value={participants.vulnerableGroups.justification}
                onChange={(e) => updateVulnerableGroups('justification', e.target.value)}
                placeholder="Explain why it is necessary to include these vulnerable groups and what additional safeguards you will put in place to protect them"
                rows={5}
              />
            </div>
          </>
        )}
      </div>

      <NavigationButtons
        onPrevious={previousStep}
        onNext={isComplete ? nextStep : null}
        disableNext={!isComplete}
        nextLabel="Continue to Recruitment"
      />
    </div>
  );
}

export default D4_Participants;
